import React, { useEffect, useState } from "react";
import { DailyIcon } from "@/features/daily/components/DailyIcon";

type Props = {
    id: string;
    label: string;
    lines: string[];
};

/**
 * An info tooltip that opens on hover AND on keyboard focus, is tied to its trigger
 * with `aria-describedby`, and closes on Escape without moving focus (WCAG 1.4.13,
 * 2.4.3) — it shows again once the pointer leaves or focus moves on.
 */
export const DailyTip: React.FC<Props> = ({ id, label, lines }) => {
    const [dismissed, setDismissed] = useState(false);
    const [hovered, setHovered] = useState(false);

    // WCAG 1.4.13: a bubble opened by the pointer is dismissible too — Escape anywhere while it shows.
    useEffect(() => {
        if (!hovered) return undefined;
        const onKey = (event: KeyboardEvent): void => { if (event.key === "Escape") setDismissed(true); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [hovered]);

    return (
        <span
            className="daily-tip"
            data-dismissed={dismissed}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => { setHovered(false); setDismissed(false); }}
        >
            <button
                type="button"
                className="daily-tip__trigger"
                aria-label={label}
                aria-describedby={id}
                onKeyDown={(event) => { if (event.key === "Escape") setDismissed(true); }}
                onBlur={() => setDismissed(false)}
            >
                <DailyIcon name="info" />
            </button>
            <span role="tooltip" id={id} className="daily-tip__bubble">
                {lines.map((line, index) => <span key={`${index}:${line}`} className="daily-tip__line">{line}</span>)}
            </span>
        </span>
    );
};
